import { useContext, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledInputSelect } from '@components/controlled-input-select';
import { FIELD_QUANTITY, REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/src/utils';
import { IconBoxOffice } from '@components/icons';
import { InputCounter } from '@components/input-counter';
import { InputSelectAddons } from '@components/input-select-addons';
import { InputSelectAddonsMode } from '@components/input-select-addons/src/input-select-addons.type';
import { InputSelectAdmission } from '@components/input-select-admission';
import { Loader } from '@components/loader';
import { ModalStickyFooter } from '@components/modals/modal-sticky-footer';
import { ModalStickyHeader } from '@components/modals/modal-sticky-header';
import { MultiselectOption } from '@components/multiselect/multiselect.type';
import { BoxOfficeForm } from '@components/process-box-office/src/process-box-office.type';
import { SORT_ASC } from '@constants/global';
import { CompanyAuthContext } from '@contexts/company-auth-context/company-auth-context';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { Admission } from '@domain/admission';
import { ActiveChannel } from '@domain/channel';
import { CompanyRoles } from '@domain/company';
import { EventListItem } from '@domain/event';
import { useGetOfferAddon } from '@hooks/addons/use-get-offer-addons';
import { AdmissionResponse, useGetAdmissions } from '@hooks/admissions/use-get-admissions';
import { useGetChannels } from '@hooks/events/use-get-event-channels';
import { useGetEventList } from '@hooks/events/use-get-event-list';
import { useGetActiveChannels } from '@hooks/use-active-channels';
import { EventView } from '@hooks/use-event-navigation/use-event-navigation';
import { Divider } from '@tremor/react';
import { calculateChannelMaxQuantityPerTransaction } from '@utils/calculate-max-items-per-transation/calculate-max-items-per-transation';
import { formatDateName } from '@utils/format-date/format-date';
import moment from 'moment';

import { shouldFilterOfferIfIsSiae } from '../utils/filter-offer-if-siae';
import { filterOfferBasedOnRole } from '../utils/filter-offer-per-role';

export const SelectType = ({
  onContinue,
  offer,
}: {
  onContinue: () => void;
  offer?: Admission;
  parentRef?: React.RefObject<HTMLDivElement | HTMLFormElement>;
}) => {
  const { t } = useTranslation();
  const {
    companyClubState: { isSiaeImportLimited, isImportLimited },
    companyState: { id: companyId },
    clubState: {
      id: clubId,
      city: { timezone },
    },
  } = useContext(CompanyClubContext);
  const { role } = useContext(CompanyAuthContext);
  const {
    formState: { isValid },
    watch,
    setValue,
  } = useFormContext<BoxOfficeForm>();

  const [channelList, setChannelList] = useState<ActiveChannel[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<MultiselectOption[]>([]);

  const eventId = watch('eventId');
  const offerId = watch('offerId');
  const channelId = watch('channelId');
  const todayNow = useMemo(() => moment().tz(timezone).unix(), [timezone]);

  const {
    data: eventList,
    isInitialLoading: isEventListLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetEventList({
    companyId,
    clubId,
    view: 'all' as EventView,
    params: {
      startTime: todayNow,
      sort: SORT_ASC,
      orderBy: 'date',
      limit: 50,
    },
  });
  const eventListFiltered = eventList?.pages?.map((page) => page.filter((event) => !event.settings.isHidden));
  const { data: admissionList } = useGetAdmissions({ eventId, options: { enabled: !!eventId } });
  const { data: addOnsList } = useGetOfferAddon({ offerId, enabled: !!offerId });
  const availableAddons = addOnsList?.filter((addon) => {
    if (!addon.quantity) return true;

    return addon.soldItems < addon.quantity;
  });
  const { data: activeChannels } = useGetActiveChannels({
    offerId,
    enabled: !!offerId,
  });
  const { data: eventChannels } = useGetChannels({ eventId, enabled: !!eventId && !!offerId });

  const filterCondition = (selectedOffer: Admission) => shouldFilterOfferIfIsSiae(selectedOffer, isSiaeImportLimited);
  const filterByRole = (selectedOffer: Admission) =>
    filterOfferBasedOnRole({ role: role?.name as CompanyRoles | undefined, isImportLimited, selectedOffer });
  const filterGuestListByRole = (selectedOffer: Admission) =>
    filterOfferBasedOnRole({ role: role?.name as CompanyRoles | undefined, isImportLimited: false, selectedOffer });
  const filterNonNominative = (selectedOffer: Admission) => !selectedOffer.settings.isNominative;

  // Filter offers based on various conditions - now we support addons so no need to filter mandatory addons
  const filteredAdmissionList: AdmissionResponse = {
    bottleService:
      admissionList?.bottleService.filter(
        (bottle) => filterCondition(bottle) && filterByRole(bottle) && filterNonNominative(bottle)
      ) ?? [],
    guestList:
      admissionList?.guestList.filter(
        (guest) => filterCondition(guest) && filterGuestListByRole(guest) && filterNonNominative(guest)
      ) ?? [],
    ticket:
      admissionList?.ticket.filter(
        (ticket) => filterCondition(ticket) && filterByRole(ticket) && filterNonNominative(ticket)
      ) ?? [],
  };

  const showAddons = !!offer && offer.addOns.length > 0;

  const handleEventSelectorChange = () => {
    if (
      filteredAdmissionList.bottleService.length === 0 &&
      filteredAdmissionList.ticket.length === 0 &&
      filteredAdmissionList.guestList.length === 1
    ) {
      setValue('offerId', filteredAdmissionList.guestList[0].id, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    } else {
      setValue('offerId', '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }
  };

  const handleOfferChange = () => {
    const isDefaultChannelOnList = channelList.some((channel) => channel.id === channelId);
    setValue('addOns', [], { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setValue('channelId', isDefaultChannelOnList ? channelId : '', {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    setSelectedAddons([]);
  };

  useEffect(() => {
    if (eventChannels && activeChannels && offer) {
      const channelsFormatted = eventChannels
        .filter((channel) => channel.isEnabled)
        .map((channel) => ({
          ...channel.channel,
          maxQuantity: null,
        }));

      if (activeChannels.isForAllChannels) {
        setChannelList(channelsFormatted);
        return;
      }

      setChannelList(activeChannels.channels);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChannels, eventChannels, offer]);

  if (hasNextPage) fetchNextPage();

  return (
    <>
      <ModalStickyHeader
        title={
          <span className='flex flex-grow items-center gap-3 text-headline-m text-left text-primary'>
            <IconBoxOffice className='w-6 h-6' />
            {t('offers:boxOfficeTitle')}
          </span>
        }
      />
      <div className='flex flex-col gap-4 px-4 pb-6 sm:px-20 sm:pb-12'>
        {isEventListLoading && <Loader />}
        {!!eventListFiltered && (
          <ControlledInputSelect
            fieldRegisterName='eventId'
            options={eventListFiltered.flat().map((e: EventListItem) => ({
              ...e,
              name: formatDateName(e.name, e.startingTime, timezone),
            }))}
            rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
            onChange={handleEventSelectorChange}
            label={t('offers:selectAnEvent')}
          />
        )}
        {!!admissionList && (
          <InputSelectAdmission
            admissionList={filteredAdmissionList}
            eventId={eventId}
            selectedOffer={offer}
            onChange={handleOfferChange}
          />
        )}
        {showAddons && offer && (
          <InputSelectAddons
            addOnsList={availableAddons ?? []}
            selectedAddons={selectedAddons}
            required={offer.hasMandatoryAddOns}
            mode={offer.addOnChoiceMode as InputSelectAddonsMode}
            setSelectedAddons={setSelectedAddons}
          />
        )}
        {offer && (
          <ControlledInputSelect
            fieldRegisterName='channelId'
            rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
            options={channelList}
            // isDisabled={offer && channelList.length === 1}
            label={t('invitations:processModalChannelSelectorTitle')}
          />
        )}
        {offer && channelId && (
          <>
            <Divider className='my-2' />
            <InputCounter
              formCounterName={FIELD_QUANTITY}
              minQuantity={offer.itemsPerTransaction.min ?? 0}
              maxQuantity={calculateChannelMaxQuantityPerTransaction(offer)}
              title={t('invitations:totalGuestCounterText')}
              error={t('errors:minQuantityFieldError', { number: offer.itemsPerTransaction?.min })}
            />
          </>
        )}
      </div>
      <ModalStickyFooter
        isSubmitDisabled={!isValid}
        primaryButtonText={t('offers:continueButton')}
        onPrimaryButtonClick={onContinue}
      />
    </>
  );
};
